import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { studioCasesAPI } from '../api/studioCasesAPI';
import { useAuth } from '../../auth/hooks/useAuth';
import { sanitizeHtml } from '../../../lib/sanitizeHtml';
import { appUrl, withAppBasename } from '../../../config/appBase';

const styles = {
  page: { minHeight: '100vh', background: 'var(--iv-bg)', color: 'var(--iv-head)', fontFamily: 'var(--iv-font-body)', lineHeight: 1.5 },
  inner: { maxWidth: 'min(1100px, calc(100vw - 32px))', margin: '0 auto', padding: '48px 24px 80px', width: '100%', boxSizing: 'border-box' },
  h1: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.02em', margin: '0 0 8px' },
  sub: { fontSize: '0.875rem', color: 'var(--iv-body)', marginBottom: 32 },
  label: { display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, color: 'var(--iv-body)' },
  input: { width: '100%', boxSizing: 'border-box', border: '1px solid var(--iv-line)', padding: '10px 12px', fontSize: '0.9375rem', marginBottom: 16, background: 'var(--iv-raised)', color: 'var(--iv-head)', borderRadius: 0 },
  textarea: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid var(--iv-line)',
    padding: '10px 12px',
    fontSize: '0.9375rem',
    minHeight: 120,
    marginBottom: 16,
    background: 'var(--iv-raised)',
    color: 'var(--iv-body)',
    borderRadius: 0,
    resize: 'vertical',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'inherit',
    lineHeight: 1.5,
  },
  row: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  rowItem: { flex: '1 1 200px' },
  btn: { border: '1px solid var(--iv-head)', background: 'var(--iv-surface)', color: 'var(--iv-head)', padding: '10px 16px', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', marginRight: 8, marginBottom: 8 },
  btnPrimary: { background: 'var(--iv-btn)', color: 'var(--iv-btn-fg)' },
  btnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  err: { color: 'var(--iv-body)', background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', padding: 12, marginBottom: 16, fontSize: '0.875rem' },
  ok: { color: 'var(--iv-success)', background: 'var(--iv-surface)', border: '1px solid var(--iv-success)', padding: 12, marginBottom: 16, fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  card: { border: '1px solid var(--iv-line)', padding: 16, marginTop: 12, background: 'var(--iv-bg)' },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 },
  img: { width: '100%', border: '1px solid var(--iv-line)', display: 'block' },
  small: { fontSize: '0.78rem', color: 'var(--iv-muted)' },
  preview: { border: '1px solid var(--iv-line)', padding: 16, marginTop: 16, maxHeight: 260, overflow: 'auto', fontSize: '0.75rem', background: 'var(--iv-bg)' },
  toolbar: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 },
  toolBtn: { border: '1px solid var(--iv-line)', background: 'var(--iv-surface)', color: 'var(--iv-head)', padding: '4px 8px', fontSize: '0.75rem', cursor: 'pointer' },
  editor: {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    border: '1px solid var(--iv-line)',
    padding: '16px 20px',
    fontSize: '0.9375rem',
    minHeight: 120,
    marginBottom: 16,
    background: 'var(--iv-raised)',
    color: 'var(--iv-body)',
    outline: 'none',
    whiteSpace: 'normal',
    wordBreak: 'normal',
    overflowWrap: 'break-word',
  },
  textareaAsInput: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid var(--iv-line)',
    padding: '10px 12px',
    fontSize: '0.9375rem',
    minHeight: 44,
    marginBottom: 16,
    background: 'var(--iv-raised)',
    color: 'var(--iv-body)',
    borderRadius: 0,
    fontFamily: 'inherit',
    lineHeight: 1.5,
    resize: 'vertical',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  textareaActTitle: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid var(--iv-line)',
    padding: '10px 12px',
    fontSize: '0.9375rem',
    minHeight: 40,
    marginBottom: 16,
    background: 'var(--iv-raised)',
    color: 'var(--iv-head)',
    borderRadius: 0,
    fontFamily: 'inherit',
    lineHeight: 1.5,
    resize: 'vertical',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  sectionTitle: {
    fontSize: '0.6875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--iv-head)',
    marginTop: 24,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '1px solid var(--iv-line)',
    fontWeight: 600,
  },
  sectionTitleFirst: { marginTop: 0 },
  readingPreviewCard: {
    border: '1px solid var(--iv-line)',
    padding: '24px 28px',
    marginTop: 12,
    background: 'var(--iv-surface)',
    maxWidth: '100%',
    overflow: 'visible',
  },
  actCaseBlock: {
    border: '1px solid var(--iv-line)',
    padding: '20px 24px',
    marginBottom: 20,
    background: 'var(--iv-surface)',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  actCaseKicker: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--iv-muted)',
    marginBottom: 8,
  },
  jsonPre: {
    border: '1px solid var(--iv-line)',
    padding: 16,
    marginTop: 8,
    maxHeight: 360,
    overflow: 'auto',
    fontSize: '0.75rem',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    lineHeight: 1.45,
    background: 'var(--iv-bg)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
};

/* Pathbuddy case HTML: airy paragraphs, <ul> quotes, <ol> with <strong>N.</strong> (no duplicate list markers). */
const STUDIO_CASE_BODY_HTML_CSS = `
.studio-case-html-body {
  font-size: 0.9375rem;
  line-height: 1.65;
  color: var(--iv-head);
  max-width: 100%;
  font-family: inherit;
}
/* One tone for body copy: lists, items, and bold option labels match paragraphs (no darker/lighter blocks). */
.studio-case-html-body p,
.studio-case-html-body ul,
.studio-case-html-body ol,
.studio-case-html-body li,
.studio-case-html-body strong,
.studio-case-html-body b,
.studio-case-html-body em,
.studio-case-html-body span {
  color: var(--iv-head);
  font-size: inherit;
  font-family: inherit;
}
.studio-case-html-body strong,
.studio-case-html-body b {
  font-weight: 600;
}
.studio-case-html-body p {
  margin: 0 0 0.9em;
}
.studio-case-html-body p:last-child {
  margin-bottom: 0;
}
.studio-case-html-body p + p {
  margin-top: 0.15em;
}
.studio-case-html-body p + ul,
.studio-case-html-body p + ol {
  margin-top: 0.35em;
}
.studio-case-html-body ul {
  margin: 0.65em 0 1em;
  padding-left: 1.85em;
  margin-left: 0.35em;
  list-style-type: disc;
}
.studio-case-html-body ul ul {
  margin: 0.35em 0 0.5em;
}
.studio-case-html-body ol {
  margin: 0.85em 0 1.1em;
  padding-left: 1.85em;
  margin-left: 0.35em;
  list-style-type: decimal;
}
.studio-case-html-body ol > li {
  margin: 0.55em 0;
  padding-left: 0.2em;
  list-style: decimal;
  list-style-type: decimal;
}
.studio-case-html-body ol ol {
  margin: 0.45em 0 0.55em;
  margin-left: 0.35em;
  padding-left: 1.65em;
}
.studio-case-html-body table {
  width: 100%;
  border-collapse: collapse;
  margin: 0.75em 0 1em;
  font-size: inherit;
}
.studio-case-html-body th,
.studio-case-html-body td {
  border: 1px solid var(--iv-line);
  padding: 8px 10px;
  text-align: left;
  vertical-align: top;
}
.studio-case-html-body th {
  font-weight: 600;
  background: var(--iv-fill);
}
.studio-case-html-body li {
  display: list-item;
}
.studio-case-html-body ul > li {
  margin: 0.4em 0;
}
.studio-case-html-body blockquote {
  margin: 0.85em 0;
  padding-left: 1.1em;
  border-left: 3px solid var(--iv-line);
  color: var(--iv-head);
}
.studio-rich-editor.studio-case-html-body {
  min-height: inherit;
}
.studio-case-reading-preview .case-preview-doc-title {
  font-size: 1.35rem;
  font-weight: 600;
  margin: 0 0 1em;
  letter-spacing: -0.02em;
  line-height: 1.25;
}
.studio-case-reading-preview .case-preview-kicker {
  font-weight: 600;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--iv-muted);
  margin: 1.25em 0 0.4em;
}
.studio-case-reading-preview .case-preview-kicker:first-of-type {
  margin-top: 0;
}
.studio-case-reading-preview .case-preview-act-block {
  margin-top: 1.75em;
}
.studio-case-reading-preview .case-preview-act-title {
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 0.65em;
  letter-spacing: -0.02em;
  line-height: 1.3;
  color: var(--iv-head);
}
.studio-case-reading-preview .case-preview-act-body {
  max-width: 100%;
}
/* Exhibit studio: borderless inputs inside collapsed-border grid (no “separate boxes”). */
.studio-exhibit-table-grid input:focus {
  outline: 2px solid var(--iv-btn);
  outline-offset: -2px;
}
.studio-exhibit-table-grid input::placeholder {
  color: var(--iv-muted);
}
`;

/** Case JSON contract: always original, never locked (no UI toggles). */
const applyStudioFlags = (c) => {
  if (!c || typeof c !== 'object') return c;
  return { ...c, is_original: true, is_locked: false };
};

const parseLinks = (text) => text.split(/\n|,/).map((s) => s.trim()).filter((s) => s.startsWith('http'));
const stripHtml = (html) => (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const escapeHtmlText = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/** Plain text → Pathbuddy-style description HTML (paragraphs + <br/> like md_to_json soft breaks). */
const plainLoglineToDescriptionHtml = (text) => {
  const t = (text || '').trim();
  if (!t) return '<p></p>';
  return t
    .split(/\n\n+/)
    .filter((p) => p.trim())
    .map((p) => `<p>${escapeHtmlText(p).replace(/\n/g, '<br/>')}</p>`)
    .join('');
};

// DOMParser, not a detached div: <img onerror> fires even in a detached div.
const parseDetached = (html) => new DOMParser().parseFromString(String(html || ''), 'text/html').body;

const htmlToPlainText = (html) => {
  if (typeof document === 'undefined') {
    return stripHtml(html);
  }
  const d = parseDetached(html);
  return (d.innerText || d.textContent || '').trim();
};

/** Exhibit / logline-style fields: paragraphs → plain, joined for textarea editing. */
const htmlDescriptionToPlain = (html) => {
  if (typeof document === 'undefined') {
    return stripHtml(html);
  }
  const d = parseDetached(html);
  const ps = d.querySelectorAll('p');
  if (ps.length) {
    return [...ps].map((p) => (p.textContent || '').trim()).join('\n\n');
  }
  return (d.textContent || '').trim();
};

const stripLeadingLabelPrefix = (text, labels) => {
  const escaped = labels.map((x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const re = new RegExp(`^\\s*(?:${escaped})\\s*:\\s*`, 'i');
  return String(text || '').replace(re, '');
};

const stripLeadingLabelPrefixHtml = (html, labels) => {
  const raw = String(html || '');
  if (!raw) return raw;
  if (typeof document === 'undefined') {
    return stripLeadingLabelPrefix(raw, labels);
  }
  const d = parseDetached(raw);
  const walker = d.ownerDocument.createTreeWalker(d, NodeFilter.SHOW_TEXT, null);
  let node = walker.nextNode();
  while (node) {
    const current = node.nodeValue || '';
    if (current.trim().length > 0) {
      node.nodeValue = stripLeadingLabelPrefix(current, labels);
      break;
    }
    node = walker.nextNode();
  }
  return d.innerHTML;
};

const sanitizeExhibitDisplayFields = (ex) => ({
  ...ex,
  notes: stripLeadingLabelPrefixHtml(ex?.notes || '', ['Notes']),
  source: stripLeadingLabelPrefixHtml(ex?.source || '', ['Source']),
});

const sanitizeCaseExhibitDisplayFields = (caseObj) => {
  if (!caseObj || !Array.isArray(caseObj.exhibits)) return caseObj;
  return {
    ...caseObj,
    exhibits: caseObj.exhibits.map((ex) => sanitizeExhibitDisplayFields(ex || {})),
  };
};

const slugify = (s) => (s || 'case').toLowerCase().replace(/[^\w-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'case';

/** Strip tags for length checks (same idea as CaseStudy exhibit filtering). */
const stripHtmlForExhibitMeasure = (html) => String(html || '').replace(/<[^>]*>/g, '').trim();

/**
 * Auto-derived exhibit flags for JSON + reader (see CaseStudy modal: HTML_data vs image_url).
 * - is_HTML: treat the exhibit as “table / HTML_data driven” when that field has enough plain text.
 * - is_image: exhibit includes an https image, or local file(s) queued for publish enhancement.
 */
const deriveExhibitAutoFields = (ex, pendingImageFileCount) => {
  const is_HTML = stripHtmlForExhibitMeasure(ex.HTML_data).length > 10;
  const url = String(ex.image_url || '').trim();
  const is_image = /^https?:\/\//i.test(url) || pendingImageFileCount > 0;
  return { is_HTML, is_image };
};

const EXHIBIT_HTML_WRAP_ID = 'studio-exhibit-table-tools-root';

const parseExhibitHtmlWrap = (html) => {
  const raw = String(html || '');
  const doc = new DOMParser().parseFromString(`<div id="${EXHIBIT_HTML_WRAP_ID}">${raw}</div>`, 'text/html');
  const wrap = doc.getElementById(EXHIBIT_HTML_WRAP_ID);
  return { doc, wrap: wrap || doc.body };
};

const exhibitCellPlainFromEl = (el) => {
  if (!el) return '';
  return (el.textContent || '').replace(/\u00a0/g, ' ');
};

/** Parse HTML_data into plain-text header/body cells (studio form only; other markup is dropped on save). */
const parseExhibitTablesModel = (html) => {
  const { wrap } = parseExhibitHtmlWrap(html);
  const tables = [];
  wrap.querySelectorAll('table').forEach((table) => {
    const theadRow = table.querySelector('thead tr');
    const headerRow = theadRow || table.querySelector('tr');
    let headers = headerRow
      ? [...headerRow.querySelectorAll('th, td')].map((c) => exhibitCellPlainFromEl(c))
      : ['Column 1', 'Column 2'];
    const rows = [];
    const tbody = table.querySelector('tbody');
    if (tbody) {
      const tbodyTrs = [...tbody.querySelectorAll('tr')];
      // Without <thead>, querySelector('tr') is the first tbody row — do not also treat it as a data row.
      const skipFirstAsHeaderOnly =
        !theadRow && headerRow && tbodyTrs.length > 0 && tbodyTrs[0] === headerRow;
      const dataTrs = skipFirstAsHeaderOnly ? tbodyTrs.slice(1) : tbodyTrs;
      dataTrs.forEach((tr) => {
        rows.push([...tr.querySelectorAll('td, th')].map((c) => exhibitCellPlainFromEl(c)));
      });
    } else {
      table.querySelectorAll('tr').forEach((tr) => {
        if (tr.closest('thead')) return;
        if (!theadRow && headerRow && tr === headerRow) return;
        rows.push([...tr.querySelectorAll('td, th')].map((c) => exhibitCellPlainFromEl(c)));
      });
    }
    let colCount = Math.max(headers.length, 1);
    rows.forEach((r) => {
      colCount = Math.max(colCount, r.length);
    });
    while (headers.length < colCount) headers.push('');
    headers = headers.slice(0, colCount);
    const normRows =
      rows.length > 0
        ? rows.map((r) => {
            const x = [...r];
            while (x.length < colCount) x.push('');
            return x.slice(0, colCount);
          })
        : [Array.from({ length: colCount }, () => '')];
    tables.push({ headers, rows: normRows });
  });
  return { tables };
};

/**
 * Serialize tables as simple HTML only (no inline cell styles).
 * Case Study renders `HTML_data` inside `.html-content`; theme tables live in CaseStudy.css — inline styles would override that.
 */
const serializeExhibitTablesModel = (model) => {
  const tables = model?.tables || [];
  if (!tables.length) return '';
  return tables
    .map((t) => {
      const colCount = Math.max(t.headers?.length || 0, 1);
      const headers = [];
      for (let i = 0; i < colCount; i += 1) headers.push(String(t.headers[i] ?? ''));
      const thRow = headers.map((h) => `<th>${escapeHtmlText(h)}</th>`).join('');
      const bodyRows = t.rows && t.rows.length ? t.rows : [Array.from({ length: colCount }, () => '')];
      const body = bodyRows
        .map((r) => {
          const cells = [];
          for (let i = 0; i < colCount; i += 1) {
            cells.push(`<td>${escapeHtmlText(String(r[i] ?? ''))}</td>`);
          }
          return `<tr>${cells.join('')}</tr>`;
        })
        .join('');
      return `<table style="border-collapse:collapse;width:100%;margin-bottom:16px"><thead><tr>${thRow}</tr></thead><tbody>${body}</tbody></table>`;
    })
    .join('');
};

const exhibitModelAddTable = (model) => ({
  tables: [...(model.tables || []), { headers: ['Column 1', 'Column 2'], rows: [['', '']] }],
});

const exhibitModelAddRow = (model, tableIndex) => ({
  tables: (model.tables || []).map((t, i) => {
    if (i !== tableIndex) return t;
    const n = Math.max(t.headers?.length || 0, 1);
    return { ...t, rows: [...(t.rows || []), Array.from({ length: n }, () => '')] };
  }),
});

const exhibitModelAddColumn = (model, tableIndex) => ({
  tables: (model.tables || []).map((t, i) => {
    if (i !== tableIndex) return t;
    return {
      headers: [...(t.headers || []), ''],
      rows: (t.rows || []).map((r) => [...r, '']),
    };
  }),
});

const exhibitModelRemoveLastColumn = (model, tableIndex) => ({
  tables: (model.tables || []).map((t, i) => {
    if (i !== tableIndex) return t;
    if ((t.headers || []).length <= 1) return t;
    return {
      headers: t.headers.slice(0, -1),
      rows: (t.rows || []).map((r) => r.slice(0, -1)),
    };
  }),
});

const exhibitModelRemoveLastRow = (model, tableIndex) => ({
  tables: (model.tables || []).map((t, i) => {
    if (i !== tableIndex) return t;
    const rows = t.rows || [];
    if (rows.length <= 1) return t;
    return { ...t, rows: rows.slice(0, -1) };
  }),
});

const exhibitModelRemoveTable = (model, tableIndex) => ({
  tables: (model.tables || []).filter((_, i) => i !== tableIndex),
});

const exhibitModelSetHeader = (model, tableIndex, colIndex, value) => ({
  tables: (model.tables || []).map((t, i) => {
    if (i !== tableIndex) return t;
    const headers = [...(t.headers || [])];
    headers[colIndex] = value;
    return { ...t, headers };
  }),
});

const exhibitModelSetCell = (model, tableIndex, rowIndex, colIndex, value) => ({
  tables: (model.tables || []).map((t, i) => {
    if (i !== tableIndex) return t;
    const rows = (t.rows || []).map((r, ri) => {
      if (ri !== rowIndex) return r;
      const nr = [...r];
      nr[colIndex] = value;
      return nr;
    });
    return { ...t, rows };
  }),
});

/** Studio exhibit grid: collapsed borders on cells; inputs are borderless inside cells. */
const exhibitStudioTableGrid = {
  width: '100%',
  borderCollapse: 'collapse',
  border: '1px solid var(--iv-line)',
  background: 'var(--iv-raised)',
  fontSize: '0.9375rem',
  fontFamily: 'inherit',
  color: 'var(--iv-body)',
};
const exhibitStudioThCell = {
  border: '1px solid var(--iv-line)',
  padding: 0,
  margin: 0,
  background: 'var(--iv-bg)',
  fontWeight: 600,
  textAlign: 'left',
  verticalAlign: 'top',
};
const exhibitStudioTdCell = {
  border: '1px solid var(--iv-line)',
  padding: 0,
  margin: 0,
  background: 'var(--iv-surface)',
  textAlign: 'left',
  verticalAlign: 'top',
};
const exhibitStudioCellInput = {
  width: '100%',
  boxSizing: 'border-box',
  border: 'none',
  outline: 'none',
  boxShadow: 'none',
  margin: 0,
  padding: '12px 14px',
  fontSize: 'inherit',
  fontFamily: 'inherit',
  fontWeight: 'inherit',
  background: 'transparent',
  color: 'inherit',
  textAlign: 'left',
  borderRadius: 0,
  WebkitAppearance: 'none',
  appearance: 'none',
};
const exhibitStudioHeaderCellInput = {
  ...exhibitStudioCellInput,
  fontWeight: 600,
};

const SUBJECT_OPTIONS = ['Medicine', 'Law', 'Investment Banking', 'Computer Science', 'Engineering', 'Geography', 'Management Consulting'];
const DIFFICULTY_OPTIONS = ['Easy', 'Medium', 'Hard'];
const EPISODE_TYPE_OPTIONS = ['Episode', 'Seasons'];
const INDUSTRY_SUGGESTIONS = {
  Medicine: ['Anatomy', 'Physiology', 'Pathology', 'Medical Tools', 'Healthcare Systems', 'Clinical Systems - Cardiovascular', 'Clinical Systems - Gastrointestinal',
     'Clinical Systems - Neurological', 'Clinical Systems - Neurological' , 'Clinical Systems - Musculoskeletal', 
     'Clinical Systems - Endocrine', 'Clinical Systems - Renal / Urology', 'Clinical Systems - Haematology', 
     'Clinical Systems - Infectious Disease', 'Pharmacology', 'Ethics & Laws', 'Public & Social Health', 'Biochemistry', 'Clinical Skills'],
  Law: ['Intellectual Property', 'Corporate Law', 'Criminal Justice', 'International Arbitration', 'Data Privacy'],
  'Investment Banking': ['Mergers & Acquisitions', 'Equity Capital Markets', 'Debt Capital Markets', 'Private Equity', 'Venture Capital'],
  'Computer Science': ['Software', 'AI/ML', 'Cybersecurity', 'Cloud Infrastructure', 'Data Platforms'],
  Engineering: ['Energy', 'Aerospace', 'Civil Infrastructure', 'Manufacturing', 'Robotics'],
  Geography: ['Climate & Environment', 'Urban Planning', 'Logistics', 'Resource Management', 'Geopolitics'],
  'Management Consulting': ['Strategy', 'Operations', 'Change Management', 'Digital Transformation', 'Business Analytics'],
};
const CLIFFHANGER_PROMPT = ['Technical', 'System', 'Human'];

/** Same three outputs as backend publish / photo_to_resize (order: small, medium, large). */
const CASE_PUBLISH_SIZES = [
  [140, 196],
  [260, 146],
  [1920, 1080],
];

/**
 * Centre crop-to-fill to exact WxH (same math as server PIL pipeline).
 * Returns PNG data URL for preview only; server still does final encode.
 */
const canvasCropToDataUrl = (img, targetW, targetH) => {
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  if (!iw || !ih) return '';
  const scale = Math.max(targetW / iw, targetH / ih);
  const cropW = targetW / scale;
  const cropH = targetH / scale;
  const sx = (iw - cropW) / 2;
  const sy = (ih - cropH) / 2;
  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, targetW, targetH);
  return canvas.toDataURL('image/png');
};

const RichEditor = ({ label, value, onChange, minHeight = 120 }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const current = ref.current.innerHTML;
    if (current === (value || '')) return;
    const safe = sanitizeHtml(value || '');
    if (current !== safe) ref.current.innerHTML = safe;
  }, [value]);

  const apply = (cmd, cmdValue = null) => {
    if (!ref.current) return;
    ref.current.focus();
    document.execCommand(cmd, false, cmdValue);
    onChange(ref.current.innerHTML);
  };

  const flush = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  return (
    <div>
      <label style={styles.label}>{label}</label>
      <div style={styles.toolbar}>
        <button type="button" style={styles.toolBtn} onClick={() => apply('bold')}><b>B</b></button>
        <button type="button" style={styles.toolBtn} onClick={() => apply('italic')}><i>I</i></button>
        <button type="button" style={styles.toolBtn} onClick={() => apply('insertUnorderedList')}>• List</button>
        <button type="button" style={styles.toolBtn} onClick={() => apply('insertOrderedList')}>1. List</button>
        <button type="button" style={styles.toolBtn} onClick={() => apply('indent')}>Indent</button>
        <button type="button" style={styles.toolBtn} onClick={() => apply('outdent')}>Outdent</button>
      </div>
      <div
        className="studio-rich-editor studio-case-html-body"
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(ref.current ? ref.current.innerHTML : '')}
        onBlur={flush}
        style={{ ...styles.editor, minHeight }}
      />
    </div>
  );
};

/** Full-document reading preview: logline + each act as one block (title + HTML body — options live inside act HTML). */
const CaseReadingPreview = ({ caseJson }) => {
  if (!caseJson) return null;
  const act = (n) => caseJson[`act_${n}`] || {};
  return (
    <div className="studio-case-reading-preview" style={styles.readingPreviewCard}>
      <div className="case-preview-doc-title">{caseJson.title || 'Untitled'}</div>
      <div className="case-preview-kicker">Logline</div>
      <div
        className="studio-case-html-body"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(caseJson.description) || '<p></p>' }}
      />
      {[1, 2, 3].map((n) => {
        const a = act(n);
        const t = (a.title || '').trim() || `Act ${n}`;
        return (
          <section key={n} className="case-preview-act-block">
            <div className="case-preview-kicker">{`Act ${n}`}</div>
            <div className="case-preview-act-title">{t}</div>
            <div
              className="studio-case-html-body case-preview-act-body"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(a.content) || '<p></p>' }}
            />
          </section>
        );
      })}
    </div>
  );
};

const StudioCasesPage = () => {
  const { user } = useAuth();
  const userName = user?.name || (user?.email ? user.email.split('@')[0] : 'student');

  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('Engineering');
  const [industry, setIndustry] = useState('General');
  const [cliffhanger, setCliffhanger] = useState(CLIFFHANGER_PROMPT[0]);
  const [difficulty, setDifficulty] = useState('Medium');
  const [episodeType, setEpisodeType] = useState('Episode');
  const [creatorName, setCreatorName] = useState('');

  useEffect(() => {
    let token = sessionStorage.getItem('pathbuddy_access_token') ||
                sessionStorage.getItem('access_token') ||
                sessionStorage.getItem('token');
    if (token) {
      if (token.includes('pathbuddy_refresh_token')) {
        const tokenEnd = token.indexOf('pathbuddy_refresh_token');
        token = tokenEnd > 0 ? token.substring(0, tokenEnd) : token;
      }
      const baseURL = studioCasesAPI.baseURL || 'http://localhost:8080';
      fetch(`${baseURL}/api/auth/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        if (data.creator_slug) {
          setCreatorName(data.creator_slug);
        } else if (data.first_name || data.last_name) {
          const fn = (data.first_name || '').toLowerCase().replace(' ', '');
          const ln = (data.last_name || '').toLowerCase().replace(' ', '');
          if (fn && ln) {
            setCreatorName(`${fn}.${ln}`);
          } else if (fn) {
            setCreatorName(fn);
          }
        } else {
          setCreatorName(userName);
        }
      })
      .catch(err => {
        console.warn("Failed to load profile for creator slug:", err);
        setCreatorName(userName);
      });
    } else {
      setCreatorName(userName);
    }
  }, [userName]);

  const [logline, setLogline] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [domainNotes, setDomainNotes] = useState('');
  const [linksText, setLinksText] = useState('');
  const [files, setFiles] = useState([]);

  const [caseJson, setCaseJson] = useState(null);
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [coverItems, setCoverItems] = useState([]);
  const [selectedCoverId, setSelectedCoverId] = useState('');
  const [promptOverride, setPromptOverride] = useState('');
  const [error, setError] = useState('');
  const [loadingCase, setLoadingCase] = useState(false);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [loadingFinalLoglines, setLoadingFinalLoglines] = useState(false);
  const [finalLoglineSuggestions, setFinalLoglineSuggestions] = useState([]);
  const [loadingCovers, setLoadingCovers] = useState(false);
  const [ragPreview, setRagPreview] = useState(null);
  const [publishHeroFile, setPublishHeroFile] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [publishHeroPreviews, setPublishHeroPreviews] = useState(null);
  const [publishPreviewLoading, setPublishPreviewLoading] = useState(false);
  const [publishPreviewFailed, setPublishPreviewFailed] = useState(false);
  const [exhibitAssets, setExhibitAssets] = useState({});
  const exhibitAssetsRef = useRef(exhibitAssets);
  exhibitAssetsRef.current = exhibitAssets;
  const [enhancingExhibitIndex, setEnhancingExhibitIndex] = useState(null);

  const [richFields, setRichFields] = useState({
    descriptionHtml: '<p></p>',
    act1Title: '',
    act1Html: '<p></p>',
    act2Title: '',
    act2Html: '<p></p>',
    act3Title: '',
    act3Html: '<p></p>',
  });

  const syncRichFromCase = useCallback((c) => {
    setRichFields({
      descriptionHtml: c?.description || '<p></p>',
      act1Title: c?.act_1?.title || '',
      act1Html: c?.act_1?.content || '<p></p>',
      act2Title: c?.act_2?.title || '',
      act2Html: c?.act_2?.content || '<p></p>',
      act3Title: c?.act_3?.title || '',
      act3Html: c?.act_3?.content || '<p></p>',
    });
  }, []);

  const syncCaseFromRich = useCallback((nextRich) => {
    setCaseJson((prev) => {
      if (!prev) return prev;
      return applyStudioFlags({
        ...prev,
        description: nextRich.descriptionHtml || '<p></p>',
        act_1: { ...prev.act_1, title: nextRich.act1Title, content: nextRich.act1Html || '<p></p>' },
        act_2: { ...prev.act_2, title: nextRich.act2Title, content: nextRich.act2Html || '<p></p>' },
        act_3: { ...prev.act_3, title: nextRich.act3Title, content: nextRich.act3Html || '<p></p>' },
      });
    });
  }, []);

  const selectedCoverForPublish = useMemo(
    () => coverItems.find((x) => x.id === selectedCoverId && x.url) || null,
    [coverItems, selectedCoverId]
  );

  useEffect(() => {
    setCaseJson((prev) => {
      if (!prev) return prev;
      const next = { ...prev, subject, industry };
      if (
        prev.subject === subject
        && prev.industry === industry
        && prev.is_original === true
        && prev.is_locked === false
      ) {
        return prev;
      }
      return applyStudioFlags(next);
    });
  }, [subject, industry]);

  useEffect(() => {
    const sourceUrl = selectedCoverForPublish?.url || '';
    if (!publishHeroFile && !sourceUrl) {
      setPublishHeroPreviews(null);
      setPublishPreviewLoading(false);
      setPublishPreviewFailed(false);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = '';
    const imageSource = sourceUrl || URL.createObjectURL(publishHeroFile);
    if (!sourceUrl) objectUrl = imageSource;
    setPublishHeroPreviews(null);
    setPublishPreviewFailed(false);
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      try {
        const list = CASE_PUBLISH_SIZES.map(([w, h]) => ({
          key: `${w}x${h}`,
          w,
          h,
          src: canvasCropToDataUrl(img, w, h),
        })).filter((row) => row.src);
        setPublishHeroPreviews(list.length ? list : null);
        if (!list.length) setPublishPreviewFailed(true);
      } catch {
        setPublishHeroPreviews(null);
        setPublishPreviewFailed(true);
      }
      setPublishPreviewLoading(false);
    };
    img.onerror = () => {
      if (!cancelled) {
        setPublishHeroPreviews(null);
        setPublishPreviewLoading(false);
        setPublishPreviewFailed(true);
      }
    };
    img.src = imageSource;
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [publishHeroFile, selectedCoverForPublish]);

  const localFallbackPromptPlan = useMemo(() => {
    if (!caseJson) return [];
    const base = caseJson.title || 'case';
    return [
      { id: 'cover-main', label: 'Main cover', prompt: promptOverride.trim() || null },
      { id: 'cover-act1', label: 'Act 1 scene', prompt: `Act I scene from ${base}. ${stripHtml(caseJson.act_1?.content).slice(0, 600)} Cinematic photorealistic 16:9, no text.` },
      { id: 'cover-act2', label: 'Act 2 scene', prompt: `Act II scene from ${base}. ${stripHtml(caseJson.act_2?.content).slice(0, 600)} Cinematic photorealistic 16:9, no text.` },
      { id: 'cover-act3', label: 'Act 3 scene', prompt: `Act III dilemma scene from ${base}. ${stripHtml(caseJson.act_3?.content).slice(0, 600)} Cinematic photorealistic 16:9, no text.` },
    ];
  }, [caseJson, promptOverride]);

  const handleGenerateCase = async () => {
    setError('');
    setLoadingCase(true);
    setCaseJson(null);
    setCoverItems([]);
    setSelectedCoverId('');
    setTitleSuggestions([]);
    setFinalLoglineSuggestions([]);
    setRagPreview(null);
    setPublishHeroFile(null);
    setPublishHeroPreviews(null);
    setPublishPreviewLoading(false);
    setPublishPreviewFailed(false);
    setPublishResult(null);
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('subject', subject);
      fd.append('industry', industry);
      fd.append('difficulty', difficulty);
      fd.append('episode_type', episodeType);
      fd.append('media_url', mediaUrl);
      fd.append('domain_notes', domainNotes);
      fd.append('is_original', 'true');
      fd.append('is_locked', 'false');
      fd.append('extra_links', JSON.stringify(parseLinks(linksText)));
      files.forEach((f) => fd.append('file', f));
      const res = await studioCasesAPI.generateCase(fd);
      if (!res.success || !res.case) throw new Error(res.error || 'Case generation failed');
      const hydrated = applyStudioFlags({
        ...res.case,
        subject: subject || res.case.subject,
        industry: industry || res.case.industry,
        title: title.trim() || res.case.title,
      });
      const sanitizedHydrated = sanitizeCaseExhibitDisplayFields(hydrated);
      setCaseJson(sanitizedHydrated);
      syncRichFromCase(sanitizedHydrated);
      setRagPreview(res.rag_preview || null);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoadingCase(false);
    }
  };

  const pollOneCover = async (taskId) => {
    for (let i = 0; i < 90; i += 1) {
      const res = await studioCasesAPI.coverStatus(taskId);
      const st = (res.status || '').toUpperCase();
      if (st === 'SUCCEEDED' && res.output_urls && res.output_urls.length) return res.output_urls[0];
      if (st === 'FAILED' || st === 'CANCELED') throw new Error(`Runway task ${st}`);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 4000));
    }
    throw new Error('Cover generation timed out');
  };

  const generateAllFourCovers = async () => {
    if (!caseJson) return;
    setError('');
    setLoadingCovers(true);
    setCoverItems([]);
    setSelectedCoverId('');
    try {
      let promptPlan = [];
      const promptPack = await studioCasesAPI.coverPrompts(caseJson);
      if (promptPack?.success && promptPack?.prompts) {
        promptPlan = [
          { id: 'cover-main', label: 'Main cover', prompt: promptPack.prompts.whole_case || '' },
          { id: 'cover-act1', label: 'Act 1 scene', prompt: promptPack.prompts.act_1 || '' },
          { id: 'cover-act2', label: 'Act 2 scene', prompt: promptPack.prompts.act_2 || '' },
          { id: 'cover-act3', label: 'Act 3 scene', prompt: promptPack.prompts.act_3 || '' },
        ];
      } else {
        promptPlan = localFallbackPromptPlan;
      }
      if (promptOverride.trim()) {
        promptPlan[0] = { ...promptPlan[0], prompt: promptOverride.trim() };
      }

      const out = [];
      for (const item of promptPlan) {
        // one-by-one to avoid timeout pressure
        // eslint-disable-next-line no-await-in-loop
        const start = await studioCasesAPI.startCover(caseJson, item.prompt);
        if (!start.success || !start.task_id) throw new Error(start.error || `Failed starting ${item.label}`);
        // eslint-disable-next-line no-await-in-loop
        const url = await pollOneCover(start.task_id);
        out.push({ ...item, url });
        setCoverItems([...out]);
      }
      setSelectedCoverId((out.find((x) => x.id === 'cover-main') || out[0] || {}).id || '');
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoadingCovers(false);
    }
  };

  const handleTitleSuggestions = async () => {
    if (!caseJson) return;
    setLoadingTitles(true);
    setError('');
    try {
      const res = await studioCasesAPI.titleSuggestions(caseJson);
      const list = Array.isArray(res.titles) ? res.titles : [];
      setTitleSuggestions(list.slice(0, 3));
    } catch (e) {
      setError(e.message || 'Title suggestions failed');
    } finally {
      setLoadingTitles(false);
    }
  };

  const handleFinalLoglineSuggestions = async () => {
    if (!caseJson) return;
    setLoadingFinalLoglines(true);
    setError('');
    try {
      const fromFinal = htmlToPlainText(richFields.descriptionHtml);
      const preFormHint = (logline || '').trim();
      const draftLogline = [fromFinal, preFormHint].filter(Boolean).join('\n\n');
      const res = await studioCasesAPI.loglineSuggestions({
        title: (caseJson.title || '').trim() || title.trim(),
        subject,
        industry,
        cliffhanger,
        draft_logline: draftLogline,
        domain_notes: domainNotes,
        media_url: mediaUrl.trim(),
      });
      const list = Array.isArray(res.loglines) ? res.loglines : [];
      setFinalLoglineSuggestions(list.slice(0, 3));
    } catch (e) {
      setError(e.message || 'Logline suggestions failed');
    } finally {
      setLoadingFinalLoglines(false);
    }
  };

  const selectCover = (id) => {
    setSelectedCoverId(id);
    setPublishHeroFile(null);
  };

  const downloadJson = async () => {
    if (!caseJson) return;
    try {
      const filename = `${slugify(caseJson.title)}.json`;
      const blob = await studioCasesAPI.downloadCaseBlob(caseJson, filename);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      setError(e.message || 'Case download failed');
    }
  };

  const downloadSelectedImage = async () => {
    const selected = coverItems.find((x) => x.id === selectedCoverId && x.url);
    if (!selected) return;
    try {
      const blob = await studioCasesAPI.downloadCoverBlobWithFilename(selected.url, `${slugify(caseJson?.title)}-${selected.id}.png`);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${slugify(caseJson?.title)}-${selected.id}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      setError(e.message || 'Image download failed');
    }
  };

  const updateRichField = (key, val) => {
    const next = { ...richFields, [key]: val };
    setRichFields(next);
    syncCaseFromRich(next);
  };

  const getExhibits = () => (Array.isArray(caseJson?.exhibits) ? caseJson.exhibits : []);

  const updateExhibitAt = (idx, patch) => {
    setCaseJson((prev) => {
      if (!prev) return prev;
      const exhibits = Array.isArray(prev.exhibits) ? [...prev.exhibits] : [];
      while (exhibits.length <= idx) {
        exhibits.push({
          title: 'Exhibit',
          description: '<p></p>',
          is_HTML: false,
          HTML_data: '',
          is_image: false,
          image_url: '',
          notes: '<p>-</p>',
          source: '<p>[ ]</p>',
        });
      }
      const merged = { ...exhibits[idx], ...patch };
      const mergedSanitized = sanitizeExhibitDisplayFields(merged);
      const pending = exhibitAssetsRef.current[idx]?.imageFiles?.filter(Boolean).length || 0;
      const auto = deriveExhibitAutoFields(mergedSanitized, pending);
      exhibits[idx] = { ...mergedSanitized, ...auto };
      return { ...prev, exhibits };
    });
  };

  useEffect(() => {
    setCaseJson((prev) => {
      if (!prev?.exhibits?.length) return prev;
      let changed = false;
      const exhibits = prev.exhibits.map((ex, idx) => {
        const pending = exhibitAssets[idx]?.imageFiles?.filter(Boolean).length || 0;
        const auto = deriveExhibitAutoFields(ex, pending);
        if (ex.is_HTML === auto.is_HTML && ex.is_image === auto.is_image) return ex;
        changed = true;
        return { ...ex, ...auto };
      });
      return changed ? { ...prev, exhibits } : prev;
    });
  }, [exhibitAssets]);

  const addExhibit = () => {
    setCaseJson((prev) => {
      if (!prev) return prev;
      const exhibits = Array.isArray(prev.exhibits) ? [...prev.exhibits] : [];
      exhibits.push({
        title: `Exhibit ${exhibits.length + 1}`,
        description: '<p></p>',
        is_HTML: false,
        HTML_data: '',
        is_image: false,
        image_url: '',
        notes: '<p>-</p>',
        source: '<p>[ ]</p>',
      });
      return { ...prev, exhibits };
    });
  };

  const deleteExhibit = (idx) => {
    setCaseJson((prev) => {
      if (!prev) return prev;
      const exhibits = Array.isArray(prev.exhibits) ? [...prev.exhibits] : [];
      if (idx < 0 || idx >= exhibits.length) return prev;
      exhibits.splice(idx, 1);
      return { ...prev, exhibits };
    });
    setExhibitAssets((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const oldIdx = Number(k);
        if (Number.isNaN(oldIdx) || oldIdx === idx) return;
        next[oldIdx > idx ? oldIdx - 1 : oldIdx] = v;
      });
      return next;
    });
  };

  const enhanceExhibit = async (idx) => {
    if (!caseJson) return;
    const ex = getExhibits()[idx];
    if (!ex) return;
    setError('');
    setEnhancingExhibitIndex(idx);
    try {
      const fd = new FormData();
      fd.append('exhibit_json', JSON.stringify(ex));
      fd.append('case_json', JSON.stringify(caseJson));
      const sourceUrls = [];
      const sourceUrl = (exhibitAssets[idx]?.sourceUrl || '').trim();
      if (sourceUrl) sourceUrls.push(sourceUrl);
      if (sourceUrls.length) fd.append('source_urls', Array.from(new Set(sourceUrls)).join('\n'));

      const srcFile = exhibitAssets[idx]?.sourceFile || null;
      if (srcFile) fd.append('source_file', srcFile, srcFile.name || `exhibit-${idx + 1}-source`);
      const imgFiles = exhibitAssets[idx]?.imageFiles || [];
      imgFiles.forEach((f, i) => {
        if (f) fd.append('source_file', f, f.name || `exhibit-${idx + 1}-image-${i + 1}`);
      });
      const res = await studioCasesAPI.enhanceExhibit(fd);
      if (!res?.success || !res?.exhibit) throw new Error(res?.error || 'Exhibit enhancement failed');
      updateExhibitAt(idx, res.exhibit);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setEnhancingExhibitIndex(null);
    }
  };

  const handlePublishCaseAndMedia = async () => {
    if (!caseJson) return;
    const selectedUrl = selectedCoverForPublish?.url || '';
    setError('');
    setPublishResult(null);
    setPublishing(true);
    try {
      const payload = applyStudioFlags({ ...caseJson });
      const fd = new FormData();
      fd.append('case_json', JSON.stringify(payload));
      fd.append('creator_name', creatorName.trim());
      if (selectedUrl) {
        fd.append('hero_image_url', selectedUrl);
      } else if (publishHeroFile) {
        fd.append('hero_image', publishHeroFile, publishHeroFile.name || 'hero.png');
      }
      Object.entries(exhibitAssets).forEach(([k, v]) => {
        const idx = Number(k);
        if (Number.isNaN(idx) || !v) return;
        if (v.imageFiles && v.imageFiles.length) {
          v.imageFiles.forEach((f) => fd.append(`exhibit_image_${idx}`, f, f.name || `exhibit-${idx + 1}.png`));
        }
      });
      const res = await studioCasesAPI.publishCaseWithMedia(fd);
      if (!res.success) {
        const issues = Array.isArray(res.issues) ? res.issues.join('\n') : '';
        throw new Error([res.error || 'Publish failed', issues].filter(Boolean).join('\n'));
      }
      setPublishResult(res);
      
      // Auto-buy (add to user's apps list)
      if (res.app_id) {
        try {
          let token = sessionStorage.getItem('pathbuddy_access_token') ||
                      sessionStorage.getItem('access_token') ||
                      sessionStorage.getItem('token');
          if (token) {
            // Extract token if in pathbuddy format
            if (token.includes('pathbuddy_refresh_token')) {
              const tokenEnd = token.indexOf('pathbuddy_refresh_token');
              token = tokenEnd > 0 ? token.substring(0, tokenEnd) : token;
            }
            const baseURL = studioCasesAPI.baseURL || 'http://localhost:8080';
            await fetch(`${baseURL}/api/apps/${res.app_id}/buy`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
          }
        } catch (buyErr) {
          console.warn("Failed to auto-add app to dashboard:", buyErr);
        }
      }

      if (res.exhibit_image_urls && typeof res.exhibit_image_urls === 'object') {
        setCaseJson((prev) => {
          if (!prev || !Array.isArray(prev.exhibits)) return prev;
          const exhibits = prev.exhibits.map((row, i) => {
            const urls = res.exhibit_image_urls[String(i)];
            if (!urls || !urls.length) return row;
            return {
              ...row,
              is_image: true,
              image_url: urls[0],
            };
          });
          return applyStudioFlags({ ...prev, exhibits });
        });
      }
    } catch (e) {
      const msg = e.message || String(e);
      const issues = e.data?.issues;
      const extra = Array.isArray(issues) && issues.length ? `\n${issues.join('\n')}` : '';
      setError(`${msg}${extra}`);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div style={styles.page}>
      <style>{STUDIO_CASE_BODY_HTML_CSS}</style>
      <div style={styles.inner}>
        <h1 style={styles.h1}>Case Studio</h1>
        {userName && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
            padding: '16px 20px',
            background: 'var(--iv-bg)',
            border: '1px solid var(--iv-line)',
            borderRadius: '8px',
            fontFamily: 'var(--iv-font-body)'
          }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--iv-body)' }}>
                Your Creator Profile
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--iv-muted)', marginTop: 4 }}>
                Share your public creations: <a
                  href={withAppBasename(`/creator/${encodeURIComponent(creatorName || userName)}`)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: 'var(--iv-accent)', textDecoration: 'underline', fontWeight: 600 }}
                >
                  {appUrl(`/creator/${creatorName || userName}`)}
                </a>
              </div>
            </div>
            <button
              type="button"
              style={{
                background: 'var(--iv-btn)',
                color: 'var(--iv-btn-fg)',
                border: 'none',
                borderRadius: '4px',
                padding: '6px 14px',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
              onClick={() => { window.open(withAppBasename(`/creator/${encodeURIComponent(creatorName || userName)}`), '_blank'); }}
            >
              Open Profile
            </button>
          </div>
        )}
        {error ? <div style={styles.err}>{error}</div> : null}

        <div style={{ width: '100%', flexBasis: '100%' }}>
          <label style={styles.label}>Title</label>
          <textarea
            style={styles.textareaAsInput}
            rows={1}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Cinematic case title"
          />
        </div>
        <div style={styles.row}>
          <div style={styles.rowItem}>
            <label style={styles.label}>Subject</label>
            <select
              style={styles.input}
              value={subject}
              onChange={(e) => {
                const nextSubject = e.target.value;
                const suggestions = INDUSTRY_SUGGESTIONS[nextSubject] || [];
                setSubject(nextSubject);
                if (industry !== 'General' && suggestions.length && !suggestions.includes(industry)) {
                  setIndustry(suggestions[0]);
                }
              }}
            >
              {SUBJECT_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div style={styles.rowItem}>
            <label style={styles.label}>Industry</label>
            <select
              style={styles.input}
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
            >
              <option value="General">General</option>
              {(INDUSTRY_SUGGESTIONS[subject] || []).map((it) => (
                <option key={it} value={it}>{it}</option>
              ))}
            </select>
          </div>
          <div style={styles.rowItem}>
            <label style={styles.label}>Cliffhanger</label>
            <select
              style={styles.input}
              value={cliffhanger}
              onChange={(e) => setCliffhanger(e.target.value)}
            >
              {CLIFFHANGER_PROMPT.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={styles.row}>
          <div style={styles.rowItem}>
            <label style={styles.label}>Difficulty</label>
            <select
              style={styles.input}
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
            >
              {DIFFICULTY_OPTIONS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div style={styles.rowItem}>
            <label style={styles.label}>Type</label>
            <select
              style={styles.input}
              value={episodeType}
              onChange={(e) => setEpisodeType(e.target.value)}
            >
              {EPISODE_TYPE_OPTIONS.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
          <div style={styles.rowItem}>
            <label style={styles.label}>Creator Nickname</label>
            <input
              style={styles.input}
              value={creatorName}
              onChange={(e) => setCreatorName(e.target.value)}
              placeholder="Creator Name"
            />
          </div>
        </div>
        <label style={styles.label}>Logline</label>
        <p style={{ ...styles.small, marginTop: -8, marginBottom: 8 }}>
          One or two sentences: premise, stakes, and hook.
        </p>
        <textarea
          style={{ ...styles.textarea, minHeight: 80 }}
          value={logline}
          onChange={(e) => setLogline(e.target.value)}
          placeholder="A concise and striking summary of your cinematic case in 1 long sentence followed by 1 short sentence."
        />
        <label style={styles.label}>Case brief</label>
        <p style={{ ...styles.small, marginTop: -8, marginBottom: 8 }}>
          Topic, setting, audience, learning goals.
        </p>
        <textarea style={{ ...styles.textarea, minHeight: 160 }} value={domainNotes} onChange={(e) => setDomainNotes(e.target.value)} placeholder="Describe the case you want: scenario, era, discipline, what students should decide or analyze..." />
        <label style={styles.label}>Primary reference (optional)</label>
        <p style={{ ...styles.small, marginTop: -8, marginBottom: 8 }}>
          Any public page (Wiki, IMDb, news article).
        </p>
        <input style={styles.input} value={mediaUrl} onChange={(e) => setMediaUrl(e.target.value)} placeholder="https://en.wikipedia.org/" />
        <label style={styles.label}>Extra links (optional)</label>
        <p style={{ ...styles.small, marginTop: -8, marginBottom: 8 }}>
          One link per line (or comma-separated) up to 12.
        </p>
        <textarea style={{ ...styles.textarea, minHeight: 90 }} value={linksText} onChange={(e) => setLinksText(e.target.value)} placeholder={'https://example.com/page-1\nhttps://example.com/page-2'} />
        <label style={styles.label}>Attach files (optional)</label>
        <p style={{ ...styles.small, marginTop: -8, marginBottom: 8 }}>
          PDF or text. 
        </p>
        <input style={{ ...styles.input, padding: 8 }} type="file" multiple accept=".pdf,.txt,.md,.csv,.json,application/pdf,text/plain" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
        <button type="button" style={{ ...styles.btn, ...styles.btnPrimary, ...(loadingCase ? styles.btnDisabled : {}) }} disabled={loadingCase} onClick={handleGenerateCase}>
          {loadingCase ? 'Generating case...' : 'Generate case'}
        </button>


        {caseJson && (
          <>
            {ragPreview ? (
              <details style={{ ...styles.card, marginTop: 12 }}>
                <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 0 }}>RAG / sources (retrieved text used for the case)</summary>
                <div style={{ ...styles.small, marginTop: 8, marginBottom: 8 }}>
                  Corpus: {ragPreview.corpus_chars} chars → {ragPreview.corpus_chunk_count} chunks · Primary assembled: {ragPreview.primary_assembled_chars} chars · Supplementary: {ragPreview.research_chars} chars
                  {ragPreview.extra_links_truncated ? ' · Note: extra links list was truncated to server max.' : ''}
                </div>
                {ragPreview.upload_max_bytes ? (
                  <div style={styles.small}>
                    Upload limit: {(ragPreview.upload_max_bytes / (1024 * 1024)).toFixed(1)} MB per file · URL fetch timeout: {ragPreview.url_fetch_timeout_sec}s each
                  </div>
                ) : null}
                {Array.isArray(ragPreview.extra_link_results) && ragPreview.extra_link_results.length > 0 ? (
                  <div style={{ marginTop: 10 }}>
                    <div style={styles.small}>Extra links</div>
                    <ul style={{ ...styles.small, margin: '6px 0 0 18px', padding: 0 }}>
                      {ragPreview.extra_link_results.map((row, i) => (
                        <li key={`${row.url}-${i}`}>
                          {row.fetched_ok ? 'OK' : 'empty/failed'} ({row.text_chars} chars): {row.url}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {Array.isArray(ragPreview.file_results) && ragPreview.file_results.length > 0 ? (
                  <div style={{ marginTop: 10 }}>
                    <div style={styles.small}>Files</div>
                    <ul style={{ ...styles.small, margin: '6px 0 0 18px', padding: 0 }}>
                      {ragPreview.file_results.map((row) => (
                        <li key={row.filename}>
                          {row.ingested ? `Ingested (${row.text_chars} chars)` : `Skipped (${row.status})`}: {row.filename}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <div style={{ marginTop: 12 }}>
                  <div style={styles.small}>Primary source excerpt (after ranking + assembly)</div>
                  <pre style={{ ...styles.preview, maxHeight: 200, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{ragPreview.primary_source_excerpt || '—'}</pre>
                </div>
                <div style={{ marginTop: 8 }}>
                  <div style={styles.small}>Supplementary excerpt</div>
                  <pre style={{ ...styles.preview, maxHeight: 160, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{ragPreview.supplementary_excerpt || '—'}</pre>
                </div>
              </details>
            ) : null}
            <div style={styles.card}>
              <div style={{ marginBottom: 8 }}>
                <button type="button" style={styles.btn} onClick={handleTitleSuggestions} disabled={loadingTitles}>
                  {loadingTitles ? 'Getting titles...' : 'Suggest 2-3 titles'}
                </button>
                <button type="button" style={styles.btn} onClick={downloadJson}>Download JSON (DEV MODE)</button>
              </div>
              {titleSuggestions.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={styles.small}>Title suggestions:</div>
                  {titleSuggestions.map((t) => (
                    <button key={t} type="button" style={styles.btn} onClick={() => { setCaseJson((p) => applyStudioFlags({ ...p, title: t })); setTitle(t); }}>
                      Use: {t}
                    </button>
                  ))}
                </div>
              )}
              <div style={{ ...styles.sectionTitle, ...styles.sectionTitleFirst }}>Section 1 — Title</div>
              <label style={styles.label}>Final title</label>
              <textarea
                style={styles.textareaAsInput}
                rows={1}
                placeholder="Enter final case title..."
                value={caseJson.title || ''}
                onChange={(e) => setCaseJson((p) => applyStudioFlags({ ...p, title: e.target.value }))}
              />
              <div style={styles.sectionTitle}>Section 2 — Logline</div>
              <div style={{ marginBottom: 8 }}>
                <button
                  type="button"
                  style={{ ...styles.btn, ...(loadingFinalLoglines ? styles.btnDisabled : {}) }}
                  disabled={loadingFinalLoglines}
                  onClick={handleFinalLoglineSuggestions}
                >
                  {loadingFinalLoglines ? 'Getting loglines...' : 'Suggest 2-3 loglines'}
                </button>
              </div>
              {finalLoglineSuggestions.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={styles.small}>Logline suggestions:</div>
                  {finalLoglineSuggestions.map((line, idx) => (
                    <button
                      key={`final-${idx}-${line.slice(0, 24)}`}
                      type="button"
                      style={styles.btn}
                      title={line}
                      onClick={() => updateRichField('descriptionHtml', plainLoglineToDescriptionHtml(line))}
                    >
                      Use: {line}
                    </button>
                  ))}
                </div>
              )}
              <RichEditor
                label="Final logline"
                value={richFields.descriptionHtml}
                onChange={(html) => updateRichField('descriptionHtml', html)}
                minHeight={90}
              />
              <p style={{ ...styles.small, marginTop: -8, marginBottom: 16 }}>
                Editable areas below use the same HTML styling as the reading preview (paragraph spacing, quote lists, Act III options inside the act body).
              </p>
              <div style={styles.sectionTitle}>Section 3 — Acts</div>
              <div style={styles.actCaseBlock}>
                <div style={styles.actCaseKicker}>Act 1</div>
                <label style={styles.label}>Act 1 title</label>
                <textarea
                  style={styles.textareaActTitle}
                  rows={1}
                  value={richFields.act1Title}
                  onChange={(e) => updateRichField('act1Title', e.target.value)}
                />
                <RichEditor
                  label="Act 1 body (HTML — case view)"
                  value={richFields.act1Html}
                  onChange={(html) => updateRichField('act1Html', html)}
                  minHeight={220}
                />
              </div>
              <div style={styles.actCaseBlock}>
                <div style={styles.actCaseKicker}>Act 2</div>
                <label style={styles.label}>Act 2 title</label>
                <textarea
                  style={styles.textareaActTitle}
                  rows={1}
                  value={richFields.act2Title}
                  onChange={(e) => updateRichField('act2Title', e.target.value)}
                />
                <RichEditor
                  label="Act 2 body (HTML — case view)"
                  value={richFields.act2Html}
                  onChange={(html) => updateRichField('act2Html', html)}
                  minHeight={220}
                />
              </div>
              <div style={styles.actCaseBlock}>
                <div style={styles.actCaseKicker}>Act 3</div>
                <label style={styles.label}>Act 3 title</label>
                <textarea
                  style={styles.textareaActTitle}
                  rows={1}
                  value={richFields.act3Title}
                  onChange={(e) => updateRichField('act3Title', e.target.value)}
                />
                <RichEditor
                  label="Act 3 body (HTML — dilemma + numbered options in one field)"
                  value={richFields.act3Html}
                  onChange={(html) => updateRichField('act3Html', html)}
                  minHeight={280}
                />
              </div>
            </div>

            <div style={styles.card}>
              <label style={styles.label}>Optional main cover prompt override</label>
              <textarea style={{ ...styles.textarea, minHeight: 90 }} value={promptOverride} onChange={(e) => setPromptOverride(e.target.value)} />
              <button type="button" style={{ ...styles.btn, ...styles.btnPrimary, ...(loadingCovers ? styles.btnDisabled : {}) }} disabled={loadingCovers} onClick={generateAllFourCovers}>
                {loadingCovers ? 'Generating cover images...' : 'Generate images'}
              </button>
              <button type="button" style={styles.btn} onClick={downloadSelectedImage} disabled={!selectedCoverId}>
                Download selected image (DEV MODE)
              </button>
              <div style={styles.small}>Select one image card below. That one is used for download and publish cover source.</div>
              <div style={styles.grid2}>
                {coverItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => selectCover(item.id)}
                    style={{
                      border: selectedCoverId === item.id ? '2px solid var(--iv-head)' : '1px solid var(--iv-line)',
                      padding: 8,
                      background: 'var(--iv-surface)',
                      cursor: 'pointer',
                    }}
                  >
                    {item.url ? <img src={item.url} alt={item.label} style={styles.img} /> : null}
                  </button>
                ))}
              </div>
              <div style={styles.sectionTitle}>Section 4 — Exhibits</div>
              <div style={{ marginBottom: 8 }}>
                <button type="button" style={styles.btn} onClick={addExhibit}>Add exhibit</button>
              </div>
              {getExhibits().map((ex, idx) => {
                const exhibitTableModel = parseExhibitTablesModel(ex.HTML_data || '');
                const tableCount = exhibitTableModel.tables.length;
                return (
                <div key={`exhibit-${idx}`} style={styles.actCaseBlock}>
                  <div style={{ ...styles.actCaseKicker, marginBottom: 12 }}>{`Exhibit ${idx + 1}`}</div>
                  <label style={styles.label}>Title</label>
                  <textarea style={styles.textareaActTitle} rows={1} value={ex.title || ''} onChange={(e) => updateExhibitAt(idx, { title: e.target.value })} />
                  <RichEditor
                    key={`exhibit-${idx}-description`}
                    label="Description (HTML — case view)"
                    value={ex.description || '<p></p>'}
                    onChange={(html) => updateExhibitAt(idx, { description: html })}
                    minHeight={120}
                  />
                  <p style={{ ...styles.small, marginTop: -8, marginBottom: 8 }}>
                    Same toolbar as acts: bold, lists, indent/outdent. JSON stores HTML.
                  </p>
                  <div style={{ marginBottom: 6 }}>
                    <label style={styles.label}>Exhibit tables</label>
                    <p style={{ ...styles.small, marginTop: 0, marginBottom: 10 }}>
                      Add tables and edit cells here.
                    </p>
                    <div style={{ ...styles.toolbar, marginBottom: 10 }}>
                      <button
                        type="button"
                        style={styles.toolBtn}
                        onClick={() =>
                          updateExhibitAt(idx, {
                            HTML_data: serializeExhibitTablesModel(
                              exhibitModelAddTable(parseExhibitTablesModel(ex.HTML_data || '')),
                            ),
                          })
                        }
                      >
                        Add table
                      </button>
                      <button
                        type="button"
                        style={styles.toolBtn}
                        onClick={() => updateExhibitAt(idx, { HTML_data: '' })}
                        disabled={tableCount === 0}
                      >
                        Clear tables
                      </button>
                    </div>
                    {tableCount === 0 ? (
                      <p style={{ ...styles.small, marginTop: 0, marginBottom: 10 }}>No tables yet — use “Add table”.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 12 }}>
                        {exhibitTableModel.tables.map((t, ti) => (
                          <div
                            key={`exhibit-${idx}-table-${ti}`}
                            style={{
                              border: '1px solid var(--iv-line)',
                              padding: '12px 14px',
                              background: 'var(--iv-bg)',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 10,
                              }}
                            >
                              <span style={{ ...styles.small, fontWeight: 600 }}>{`Table ${ti + 1}`}</span>
                              <button
                                type="button"
                                style={styles.toolBtn}
                                onClick={() =>
                                  updateExhibitAt(idx, {
                                    HTML_data: serializeExhibitTablesModel(
                                      exhibitModelAddRow(parseExhibitTablesModel(ex.HTML_data || ''), ti),
                                    ),
                                  })
                                }
                              >
                                Add row
                              </button>
                              <button
                                type="button"
                                style={styles.toolBtn}
                                onClick={() =>
                                  updateExhibitAt(idx, {
                                    HTML_data: serializeExhibitTablesModel(
                                      exhibitModelAddColumn(parseExhibitTablesModel(ex.HTML_data || ''), ti),
                                    ),
                                  })
                                }
                              >
                                Add column
                              </button>
                              <button
                                type="button"
                                style={styles.toolBtn}
                                onClick={() =>
                                  updateExhibitAt(idx, {
                                    HTML_data: serializeExhibitTablesModel(
                                      exhibitModelRemoveLastColumn(parseExhibitTablesModel(ex.HTML_data || ''), ti),
                                    ),
                                  })
                                }
                              >
                                Remove last column
                              </button>
                              <button
                                type="button"
                                style={styles.toolBtn}
                                disabled={(t.rows || []).length <= 1}
                                onClick={() =>
                                  updateExhibitAt(idx, {
                                    HTML_data: serializeExhibitTablesModel(
                                      exhibitModelRemoveLastRow(parseExhibitTablesModel(ex.HTML_data || ''), ti),
                                    ),
                                  })
                                }
                              >
                                Remove last row
                              </button>
                              <button
                                type="button"
                                style={styles.toolBtn}
                                onClick={() => {
                                  const next = exhibitModelRemoveTable(parseExhibitTablesModel(ex.HTML_data || ''), ti);
                                  updateExhibitAt(idx, {
                                    HTML_data: serializeExhibitTablesModel(next),
                                  });
                                }}
                              >
                                Delete this table
                              </button>
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                              <table className="studio-exhibit-table-grid" style={exhibitStudioTableGrid}>
                                <thead>
                                  <tr>
                                    {t.headers.map((h, ci) => (
                                      <th key={`h-${ti}-${ci}`} style={{ ...exhibitStudioThCell, minWidth: 96 }}>
                                        <input
                                          style={exhibitStudioHeaderCellInput}
                                          value={h}
                                          aria-label={`Table ${ti + 1} header ${ci + 1}`}
                                          onChange={(e) =>
                                            updateExhibitAt(idx, {
                                              HTML_data: serializeExhibitTablesModel(
                                                exhibitModelSetHeader(
                                                  parseExhibitTablesModel(ex.HTML_data || ''),
                                                  ti,
                                                  ci,
                                                  e.target.value,
                                                ),
                                              ),
                                            })
                                          }
                                        />
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {t.rows.map((row, ri) => (
                                    <tr key={`r-${ti}-${ri}`}>
                                      {row.map((cell, ci) => (
                                        <td key={`c-${ti}-${ri}-${ci}`} style={exhibitStudioTdCell}>
                                          <input
                                            style={exhibitStudioCellInput}
                                            value={cell}
                                            aria-label={`Table ${ti + 1} row ${ri + 1} column ${ci + 1}`}
                                            onChange={(e) =>
                                              updateExhibitAt(idx, {
                                                HTML_data: serializeExhibitTablesModel(
                                                  exhibitModelSetCell(
                                                    parseExhibitTablesModel(ex.HTML_data || ''),
                                                    ti,
                                                    ri,
                                                    ci,
                                                    e.target.value,
                                                  ),
                                                ),
                                              })
                                            }
                                          />
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <label style={styles.label}>Exhibit image URL (read-only; filled from Spaces after publish)</label>
                  <div style={{ ...styles.small, marginBottom: 8, wordBreak: 'break-all' }}>
                    {(ex.image_url || '').trim().startsWith('http') ? ex.image_url : '—'}
                  </div>
                  <RichEditor
                    key={`exhibit-${idx}-notes`}
                    label="Notes (HTML — case view)"
                    value={ex.notes || '<p></p>'}
                    onChange={(html) => updateExhibitAt(idx, { notes: html || '<p></p>' })}
                    minHeight={88}
                  />
                  <RichEditor
                    key={`exhibit-${idx}-source`}
                    label="Source (HTML — case view)"
                    value={ex.source || '<p></p>'}
                    onChange={(html) => updateExhibitAt(idx, { source: html || '<p></p>' })}
                    minHeight={80}
                  />
                  <label style={styles.label}>Exhibit image (optional)</label>
                  <p style={{ ...styles.small, marginTop: -8, marginBottom: 8 }}>
                    Add image file you want to include in exhibit
                  </p>
                  <input
                    style={{ ...styles.input, padding: 8 }}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp"
                    multiple
                    onChange={(e) => {
                      const arr = Array.from(e.target.files || []);
                      setExhibitAssets((prev) => ({
                        ...prev,
                        [idx]: {
                          ...(prev[idx] || {}),
                          imageFiles: arr,
                        },
                      }));
                    }}
                  />
                  <label style={styles.label}>Enhance exhibit source URL (optional)</label>
                  <input style={styles.input} placeholder="https://... (used for exhibit enhancement)" value={exhibitAssets[idx]?.sourceUrl || ''} onChange={(e) => setExhibitAssets((prev) => ({ ...prev, [idx]: { ...(prev[idx] || {}), sourceUrl: e.target.value } }))} />
                  <label style={styles.label}>Enhance exhibit source file (optional)</label>
                  <input
                    style={{ ...styles.input, padding: 8 }}
                    type="file"
                    accept=".pdf,.txt,.md,.csv,.json,application/pdf,text/plain"
                    onChange={(e) => {
                      const file = (e.target.files && e.target.files[0]) || null;
                      setExhibitAssets((prev) => ({ ...prev, [idx]: { ...(prev[idx] || {}), sourceFile: file } }));
                    }}
                  />
                  <div>
                    <button type="button" style={{ ...styles.btn, ...(enhancingExhibitIndex === idx ? styles.btnDisabled : {}) }} disabled={enhancingExhibitIndex === idx} onClick={() => enhanceExhibit(idx)}>
                      {enhancingExhibitIndex === idx ? 'Enhancing…' : 'Enhance exhibit from link/file'}
                    </button>
                    <button type="button" style={styles.btn} onClick={() => deleteExhibit(idx)}>Delete exhibit</button>
                  </div>
                </div>
                );
              })}
            </div>

            <details open style={{ marginTop: 12 }}>
              <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 8, userSelect: 'none' }}>
                Full reading preview — HTML rendered from JSON fields (description, act_*.content) (dev mode)
              </summary>
              <p style={{ ...styles.small, marginBottom: 12 }}>
                This is not raw JSON: the browser renders the HTML strings stored inside the JSON (e.g. &lt;p&gt;, &lt;ul&gt;&lt;li&gt; quotes, &lt;ol&gt;&lt;li&gt;&lt;strong&gt;1.&lt;/strong&gt; options). New cases should get this structure from generation; edit below if needed.
              </p>
              <CaseReadingPreview caseJson={caseJson} />
            </details>
            <details style={{ marginTop: 8 }}>
              <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 8, userSelect: 'none' }}>
                Raw JSON (entire case object) (DEV MODE)
              </summary>
              <pre style={styles.jsonPre}>{JSON.stringify(caseJson, null, 2)}</pre>
            </details>

            <div style={{ ...styles.card, marginTop: 20 }}>
              <div style={{ ...styles.sectionTitle, ...styles.sectionTitleFirst }}>Publish</div>
              <p style={{ ...styles.small, marginTop: 0, marginBottom: 8 }}>
                Select one generated image above, upload a cover image below, or leave it blank to automatically assign a beautiful stock cover photo matching the subject.
              </p>
              <label style={styles.label}>Case cover image (optional)</label>
              <p style={{ ...styles.small, marginTop: -8, marginBottom: 8 }}>
                PNG, JPEG, or WebP.
              </p>
              <input
                style={{ ...styles.input, padding: 8 }}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp"
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0];
                  setPublishHeroFile(f || null);
                  if (f) setSelectedCoverId('');
                  setPublishResult(null);
                  setPublishPreviewFailed(false);
                  if (f) {
                    setPublishPreviewLoading(true);
                    setPublishHeroPreviews(null);
                  } else {
                    setPublishPreviewLoading(false);
                    setPublishHeroPreviews(null);
                  }
                }}
              />
              {publishPreviewLoading ? (
                <p style={{ ...styles.small, marginTop: 10 }}>Building previews…</p>
              ) : null}
              {publishHeroPreviews && publishHeroPreviews.length > 0 ? (
                <div style={{ marginTop: 14 }}>
                  <div style={styles.label}>How the three uploads will look (centre crop, exact pixels)</div>
                  <p style={{ ...styles.small, marginTop: 4, marginBottom: 10 }}>
                    Same geometry as the server; only PNG compression may differ slightly. The 1920×1080 tile is scaled down here for the page.
                  </p>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                      gap: 12,
                      alignItems: 'start',
                    }}
                  >
                    {publishHeroPreviews.map((row) => (
                      <div
                        key={row.key}
                        style={{
                          border: '1px solid var(--iv-line)',
                          padding: 8,
                          background: 'var(--iv-surface)',
                        }}
                      >
                        <div style={{ ...styles.small, marginBottom: 6, fontWeight: 600 }}>
                          {row.w}×{row.h}
                        </div>
                        <img
                          src={row.src}
                          alt={`Preview ${row.key}`}
                          style={{
                            width: '100%',
                            maxWidth: row.w >= 1000 ? 320 : '100%',
                            height: 'auto',
                            display: 'block',
                            border: '1px solid var(--iv-line)',
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {publishPreviewFailed ? (
                <p style={{ ...styles.small, marginTop: 10, color: 'var(--iv-muted)' }}>
                  Could not build previews for this file. If it is a valid PNG, JPEG, or WebP, you can still try Publish.
                </p>
              ) : null}
              {publishResult ? (
                <div style={{ ...styles.ok, marginTop: 12, padding: '20px 24px', borderRadius: '4px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: 12, color: 'var(--iv-success)' }}>
                    🎉 Case study successfully published and automatically added to your dashboard!
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--iv-muted)', marginBottom: 16 }}>
                    <strong>Case ID:</strong> {publishResult.case_study_id} | <strong>Slug:</strong> {publishResult.slug}
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      style={{ ...styles.btn, ...styles.btnPrimary, margin: 0 }}
                      onClick={() => { window.location.href = withAppBasename(`/case-study/${publishResult.case_study_id}`); }}
                    >
                      Study Case Study Now
                    </button>
                    <button
                      type="button"
                      style={{ ...styles.btn, margin: 0 }}
                      onClick={() => { window.location.href = withAppBasename('/apps'); }}
                    >
                      Go to Apps
                    </button>
                  </div>
                </div>
              ) : null}
              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  style={{
                    ...styles.btn,
                    ...styles.btnPrimary,
                    ...(publishing ? styles.btnDisabled : {}),
                  }}
                  disabled={publishing}
                  onClick={handlePublishCaseAndMedia}
                >
                  {publishing ? 'Publishing…' : 'Publish'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StudioCasesPage;
